<template>
  <div class="p-6 dark:text-gray-50" v-if="canAddSSHCollector || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <h1 class="text-2xl font-semibold dark:text-white">Add New SSH Runner Configuration</h1>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md dark:text-bg-gray dark:border-gray-800 p-6 rounded-lg shadow-sm border border-gray-200">

      <!-- Link Type Selection -->
      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">
          Link To<span class="pl-1 text-red-500">*</span>
        </label>
        <div class="flex space-x-4">
          <label class="inline-flex items-center">
            <input
              type="radio"
              v-model="linkType"
              value="probe"
              class="form-radio h-4 w-4 text-blue-600"
              @change="handleLinkTypeChange"
            />
            <span class="ml-2">Probe</span>
          </label>
          <label class="inline-flex items-center">
            <input
              type="radio"
              v-model="linkType"
              value="site"
              class="form-radio h-4 w-4 text-blue-600"
              @change="handleLinkTypeChange"
            />
            <span class="ml-2">Site</span>
          </label>
        </div>
        <FieldError :message="fieldErrors.link_type" />
      </div>

      <!-- Probe Selection (when linkType is 'probe') -->
      <div v-if="linkType === 'probe'" class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">
          Probe<span class="pl-1 text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="formData.probe"
          :options="probes"
          value-key="id"
          label-key="name"
          placeholder="Select a probe..."
          search-placeholder="Search probes..."
          searchable
          :error="!!fieldErrors.probe"
        />
        <FieldError :message="fieldErrors.probe" />
      </div>

      <!-- Site Selection (when linkType is 'site') -->
      <div v-if="linkType === 'site'" class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">
          Site<span class="pl-1 text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="formData.site"
          :options="sites"
          value-key="id"
          label-key="name"
          placeholder="Select a site..."
          search-placeholder="Search sites..."
          searchable
          :error="!!fieldErrors.site"
        />
        <FieldError :message="fieldErrors.site" />
      </div>

      <!-- Site-specific additional fields -->
      <div v-if="linkType === 'site'" class="border-l-4 border-green-500 pl-4 space-y-4">
        <h3 class="font-medium text-gray-700 dark:text-gray-300">Site Settings</h3>
        <div class="flex flex-col">
          <label for="influxdb_url" class="mb-1 font-medium dark:text-white">InfluxDB URL</label>
          <input
            id="influxdb_url"
            v-model="formData.influxdb_url"
            type="url"
            placeholder="https://influxdb.example.com"
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FieldError :message="fieldErrors.influxdb_url" />
        </div>
        <div class="flex items-center space-x-2">
          <input
            id="ha_cluster"
            v-model="formData.ha_cluster"
            type="checkbox"
            class="form-checkbox h-4 w-4 text-blue-600"
          />
          <label for="ha_cluster" class="font-medium dark:text-white">HA Cluster</label>
          <FieldError :message="fieldErrors.ha_cluster" />
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end space-x-2">
        <button
          type="button"
          @click="$router.push({ name: 'SSHCollectorConfigs' })"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Create SSH Runner Config
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError
    v-if="permsLoaded && !canAddSSHCollector && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createSSHCollectorConfig } from "@/services/sshCollectorService";
import { getAllProbes } from "@/services/probeService";
import { getSites } from "@/services/siteService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddSSHCollector",
  components: {
    FieldError,
    PageError,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      errorMessages: errorMessages,
      fieldErrors: {},
      linkType: "probe",
      formData: {
        probe: null,
        site: null,
        influxdb_url: "",
        ha_cluster: false,
      },
      probes: [],
      sites: [],
      canAddSSHCollector: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
  },
  async created() {
    try {
      await this.tenant.fetchOrganizations();
      await this.tenant.loadInitialData(this.fetchData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchData(schemaName = null) {
      const [probes, sites] = await Promise.all([
        getAllProbes(schemaName),
        getSites(schemaName),
      ]);
      this.probes = probes;
      this.sites = sites;
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddSSHCollector = await useStore.hasPermission("Can add SSH Collector Config");
      }
    },
    handleLinkTypeChange() {
      this.formData.probe = null;
      this.formData.site = null;
      this.fieldErrors = {};
    },
    async submitForm() {
      this.fieldErrors = {};

      if (this.linkType === "probe" && !this.formData.probe) {
        this.fieldErrors.probe = "Please select a probe";
        return;
      }
      if (this.linkType === "site" && !this.formData.site) {
        this.fieldErrors.site = "Please select a site";
        return;
      }

      try {
        const configData = {};

        if (this.linkType === "probe") {
          configData.probe = this.formData.probe;
        } else {
          configData.site = this.formData.site;
          if (this.formData.influxdb_url) {
            configData.influxdb_url = this.formData.influxdb_url;
          }
          configData.ha_cluster = this.formData.ha_cluster;
        }

        const newConfig = await createSSHCollectorConfig(configData, this.tenant.currentSchemaName.value);
        this.$router.push({
          name: "SSHCollectorDetails",
          params: { id: newConfig.id },
          query: this.tenant.addSchemaParam(),
        });
      } catch (error) {
        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            if (Array.isArray(errData[key])) {
              this.fieldErrors[key] = errData[key][0];
            } else {
              this.fieldErrors[key] = errData[key];
            }
          });
        }
        console.error("Error creating SSH collector config:", error);
      }
    },
  },
};
</script>
